import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ExploreClasses = ({ activeCategory, onClose }) => {
  const [loading, setLoading] = useState(true);

  // Trigger skeleton loader whenever the category changes
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1200); 
    return () => clearTimeout(timer);
  }, [activeCategory]);

  // Mock Data - Replace with your actual database/API data
  const allClassData = [
    { id: 1, name: 'Grade 10 Mathematics', teacher: 'Mr. Bandara', category: 'O/L', price: '2500', image: 'https://images.unsplash.com/photo-1509228468518-180dd48a5791?auto=format&fit=crop&q=80&w=400' },
    { id: 2, name: 'Combined Maths 2026', teacher: 'Mr. Sameera', category: 'A/L', price: '3500', image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&q=80&w=400' },
    { id: 3, name: 'Grade 7 Science', teacher: 'Ms. Perera', category: 'Secondary', price: '2000', image: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&q=80&w=400' },
    { id: 4, name: 'A/L Physics Theory', teacher: 'Mr. Nimal', category: 'A/L', price: '3000', image: 'https://images.unsplash.com/photo-1636466497217-26a8cbeaf0aa?auto=format&fit=crop&q=80&w=400' },
    { id: 5, name: 'Grade 11 ICT', teacher: 'Mr. Silva', category: 'O/L', price: '2200', image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&q=80&w=400' },
    { id: 6, name: 'Grade 10 Science', teacher: 'Ms. Kumara', category: 'O/L', price: '2000', image: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&q=80&w=400' },
    { id: 7, name: 'Primary English', teacher: 'Ms. Silva', category: 'Primary', price: '1500', image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&q=80&w=400' },
  ];

  // Logic to filter classes based on the selected category from Home
  const filteredClasses = activeCategory === 'All' 
    ? allClassData 
    : allClassData.filter(c => c.category === activeCategory);

  // Skeleton Loader UI Component (6 cards)
  const SkeletonCard = () => (
    <div className="bg-white rounded-[2.5rem] p-5 shadow-sm border border-gray-100 animate-pulse">
      <div className="bg-gray-200 h-48 rounded-[2rem] mb-4"></div>
      <div className="h-6 bg-gray-200 rounded-full w-3/4 mb-3"></div>
      <div className="h-4 bg-gray-200 rounded-full w-1/2 mb-6"></div>
      <div className="flex justify-between items-center">
        <div className="h-8 bg-gray-200 rounded-lg w-20"></div>
        <div className="h-12 bg-gray-200 rounded-2xl w-12"></div>
      </div>
    </div>
  );

  return (
    <section className="py-20 bg-slate-50 border-y-4 border-indigo-100/50 relative overflow-hidden">
      
      {/* Floating Close Button */}
      <button 
        onClick={onClose}
        className="absolute top-8 right-8 z-30 bg-white shadow-xl text-gray-400 hover:text-red-600 w-12 h-12 rounded-full flex items-center justify-center transition-all border border-gray-100 hover:scale-110 active:scale-95 group"
      >
        <span className="text-2xl font-light group-hover:rotate-90 transition-transform duration-300">✕</span>
      </button>

      <div className="max-w-7xl mx-auto px-6">
        
        {/* Section Header */}
        <div className="mb-16">
          <motion.div 
            initial={{ opacity: 0, x: -20 }} 
            animate={{ opacity: 1, x: 0 }}
          >
            <h2 className="text-3xl md:text-5xl font-black text-indigo-950 uppercase tracking-tight">
              {activeCategory} <span className="text-red-600">Classes</span>
            </h2>
            <div className="w-20 h-2 bg-indigo-950 mt-4 rounded-full"></div>
            <p className="mt-6 text-gray-500 font-medium max-w-2xl">
              Browse through our available programs and expert-led sessions tailored for your success.
            </p>
          </motion.div>
        </div>

        {/* Classes Display Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          <AnimatePresence mode="wait">
            {loading ? (
              // Display 6 skeleton cards while loading
              [...Array(6)].map((_, i) => <SkeletonCard key={i} />)
            ) : (
              // Display filtered results
              filteredClasses.length > 0 ? (
                filteredClasses.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-[2.5rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100 group flex flex-col h-full"
                  >
                    <div className="relative h-52 overflow-hidden">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      <div className="absolute top-4 left-4 bg-indigo-950 px-4 py-1.5 rounded-full text-[10px] font-black text-white uppercase tracking-widest">
                        {item.category}
                      </div>
                    </div>
                    
                    <div className="p-8 flex flex-col flex-grow">
                      <h3 className="text-xl font-black text-indigo-950 leading-tight group-hover:text-red-600 transition-colors h-14">
                        {item.name}
                      </h3>
                      <p className="text-gray-400 text-sm mt-2 font-bold italic">By {item.teacher}</p>
                      
                      <div className="mt-auto pt-6 flex justify-between items-center border-t border-gray-50">
                        <div>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Fee</p>
                          <p className="text-2xl font-black text-indigo-950">Rs.{item.price}</p>
                        </div>
                        <button className="bg-indigo-950 text-white p-4 rounded-2xl hover:bg-red-600 transition-all shadow-md">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                // Empty State
                <div className="col-span-full py-24 text-center bg-white rounded-[2.5rem] border-2 border-dashed border-gray-100">
                  <p className="text-gray-400 text-xl font-bold">No courses available in this category yet.</p>
                </div>
              )
            )}
          </AnimatePresence>
        </div>

        {/* Bottom Navigation */}
        <div className="mt-16 flex justify-center border-t border-gray-100 pt-12">
          <button 
            onClick={onClose}
            className="group flex items-center gap-3 px-10 py-4 bg-white border border-gray-200 text-gray-400 font-black rounded-2xl hover:bg-red-50 hover:text-red-600 hover:border-red-100 transition-all uppercase tracking-widest text-xs"
          >
            <span>Close and Go Back</span>
            <svg className="w-4 h-4 group-hover:-translate-y-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 15l7-7 7 7" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default ExploreClasses;